import React from "react";
import { Camera, User, Building, Upload } from "lucide-react";
import Image from "next/image";

interface ImagenPerfil {
  tipo: string;
  nombre: string;
  archivo: string;
}

interface ImageUploaderProps {
  imagenPerfil: ImagenPerfil;
  handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isImagenValid: boolean;
  formSubmitted: boolean;
  type?: "usuario" | "escuela" | "instructor";
  className?: string;
  required?: boolean;
}

const ImageUploader = ({
  imagenPerfil,
  handleImageUpload,
  isImagenValid,
  formSubmitted,
  type = "usuario",
  className = "",
  required = true,
}: ImageUploaderProps) => {
  const getIconComponent = () => {
    switch (type) {
      case "escuela":
        return <Building className="w-12 h-12 text-blue-400" />;
      case "instructor":
      case "usuario":
      default:
        return <User className="w-12 h-12 text-blue-400" />;
    }
  };

  const getErrorMessage = () => {
    switch (type) {
      case "escuela":
        return "El logo de la escuela es requerido";
      case "instructor":
        return "La foto de perfil es requerida";
      case "usuario":
      default:
        return "La foto de perfil es requerida";
    }
  };

  // Estilos para el componente de instructor que son diferentes
  if (type === "instructor") {
    return (
      <div className={`relative ${className}`}>
        <div className="flex flex-col items-center">
          <label
            htmlFor="avatar-upload"
            className={`block relative w-[8rem] h-[8rem] md:w-32 md:h-32 rounded-full cursor-pointer overflow-hidden group transition-all duration-300 ${!isImagenValid && formSubmitted && required ? 'ring-2 ring-red-500' : 'ring-2 ring-blue-100 hover:ring-blue-300'}`}
          >
            {imagenPerfil.archivo ? (
              <div className="w-full h-full relative">
                <Image
                  src={imagenPerfil.archivo}
                  alt="Foto de perfil"
                  fill
                  className="object-cover rounded-full"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center rounded-full">
                  <div className="bg-white p-2.5 rounded-full shadow-lg transform transition-transform duration-300 group-hover:scale-110">
                    <Camera className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center flex-col rounded-full border border-blue-100 shadow-inner">
                <Upload className="w-6 xl:w-14 h-6 xl:h-14 text-blue-500 mb-2" />
                <p className="text-xs md:text-sm text-blue-600 font-medium px-2 text-center">Subir foto</p>
                {required && <span className="text-xs text-red-500 mt-1">*</span>}
              </div>
            )}
          </label>
          
          <button
            onClick={() => document.getElementById("avatar-upload")?.click()}
            className="mt-4 text-sm text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 px-5 py-2 rounded-full  shadow-md hover:shadow-lg transform hover:scale-105 transition-transform duration-200"
            type="button"
          >
            {imagenPerfil.archivo ? "Cambiar foto" : "Seleccionar foto"}
          </button>
          
          <input
            type="file"
            id="avatar-upload"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          
          {!isImagenValid && formSubmitted && required && (
            <p className="text-red-500 text-xs mt-2 text-center">
              {getErrorMessage()}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Estilos para usuario y escuela
  return (
    <div className={`relative group ${className}`}>
      <div className="w-[8rem] h-[8rem] md:w-32 md:h-32 rounded-full overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-100 transition-transform duration-300 group-hover:scale-105 shadow-md">
        {imagenPerfil.archivo ? (
          <img
            src={imagenPerfil.archivo}
            alt={type === "escuela" ? "Logo de la escuela" : "Foto de perfil"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center flex-col">
            {getIconComponent()}
            {required && <span className="text-xs text-red-500 mt-1">*</span>}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="bg-white p-2.5 rounded-full shadow-lg transform transition-transform duration-300 group-hover:scale-110">
            <Camera className="w-5 h-5 text-blue-600" />
          </div>
        </div>
      </div>
      <label
        htmlFor="avatar-upload"
        className="absolute bottom-0 right-0 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-2 rounded-full cursor-pointer shadow-md transition-all duration-200 transform hover:scale-110"
      >
        <Camera className="w-4 h-4" />
        <input
          id="avatar-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
      </label>
      {!isImagenValid && formSubmitted && required && (
        <p className="text-red-500 text-xs mt-1 text-center">
          {getErrorMessage()}
        </p>
      )}
    </div>
  );
};

export default ImageUploader; 